import PropTypes from 'prop-types';
import { useState, useEffect, useReducer } from 'react';
import BEMHelper from 'react-bem-helper';
import SavingStatus from './SavingStatus.js';
import InformationCheckbox from './InformationCheckbox.js';
import InformationDurationInput from './InformationDurationInput.js';
import OrganizationPreferenceService from './OrganizationPreferenceService';
import DefaultTokenExpirationConfiguration from './DefaultTokenExpirationConfiguration';
import UserAnalyticsService from '../../../../legacy/app/userAnalytics/UserAnalyticsService';
import { AngularInjector } from '../../../../common/AngularUtils';
import { ngRoute } from '../../../../app/routing/ngRoute';
import * as Sitemaps from '../../../routing/Sitemaps';
import { PageHeaderWrapper } from '../../../header/PageHeaderWrapper';
import DefaultSsoRoleConfiguration from './DefaultSsoRoleConfiguration';
import { ThemeProvider } from '@splunk/olly-common';
import { useLegacyThemeServiceKey } from '../../../../common/theme/ThemeProvider';
import Heading from '@splunk/react-ui/Heading';
import { StepLayout } from '@splunk/olly-common/Wizard';
import { ProtectedComponentWrapper } from '../../../../common/ProtectedComponentUtils';
import { CurrentUserStoreProvider } from '@splunk/olly-services/lib/services/CurrentUser/CurrentUserStoreProvider';
import { HTTPMethod } from '@splunk/olly-services';
import ForbiddenComponent from '@splunk/olly-common/ForbiddenComponent/ForbiddenComponent';

const ORGANIZATION_SETTINGS_TITLE = 'General Organization Settings';
const ALERT_MANAGEMENT_TITLE = 'Alert management';
const TOKEN_MANAGEMENT_TITLE = 'Token management';
const TEAM_MANAGEMENT_TITLE = 'Team management';
const RELATED_CONTENT_MANAGEMENT_TITLE = 'Related content management';

//buttons
const APPLY_CHANGES = 'Apply';
const CANCEL = 'Cancel';

//headings and labels
const TEAM_MANAGEMENT_INFORMATION = [
    'Enable enhanced team security by restricting users from joining any team.',
    'Team managers and administrators can add and remove users from a team.',
];
const TEAM_CHECKBOX_LABEL = ['Restrict team access'];

const RC_CHECKBOX_LABEL = ['Activate related content for logs'];
const RC_MANAGEMENT_INFORMATION = [
    'Activate related content searches for logs to trigger by default.',
];

const LOGS_RCMAPPINGS_CONSUMPTION_LABEL = [
    'Activate related content mappings consumption for logs',
];
const LOGS_RCMAPPINGS_CONSUMPTION_INFORMATION = [
    'Activate related content mappings consumption for logs to trigger by default.',
];

const ALERT_AUTO_CLEAR_LABEL = 'Auto-clear alerts';
const ALERT_AUTO_CLEAR_INFORMATION = [
    'Clear active alerts by default if metric time series has not responded for the given duration.',
];

const ACCESS_TOKENS_LABEL = 'Token expiration duration default';
const ACCESS_TOKEN_INFORMATION =
    'Default token expiration settings for organization members during token creation and rotation process, maximum 18 years.';

// AI Assistant constants
const AI_ASSISTANT_MANAGEMENT_TITLE = 'AI management';
const ENABLE_AI_ASSISTANT_LABEL = ['Activate AI Assistant and additional AI features'];
const ENABLE_AI_ASSISTANT_DATA_SHARING = [
    <>
        Allow Splunk to use your AI Service Data for Training and Fine-Tuning as defined in the
        <br />
        <a
            href="https://www.splunk.com/en_us/legal/splunk-specific-terms.html"
            target="_blank"
            rel="noopener noreferrer"
        >
            Splunk AI Features Specific Offering Terms
        </a>
    </>,
];
const ENABLE_AI_ASSISTANT_INFORMATION = [
    <>
        Activate the AI Assistant and additional AI features across Splunk Observability Cloud for
        your
        <br />
        organization, providing intelligent troubleshooting and faster chart building. These
        features may
        <br />
        be activated by default. To deactivate them, contact Splunk Support.{' '}
        <a
            href="https://www.splunk.com/en_us/legal/splunk-general-terms.html"
            target="_blank"
            rel="noopener noreferrer"
        >
            Splunk General Terms
        </a>
    </>,
];
const ENABLE_DATA_SHARING_INFORMATION = [
    <>
        <div style={{ marginTop: '8px', marginBottom: '4px' }}>
            <strong>Use of Data for Training and Fine-Tuning</strong>
        </div>
        Allowing Splunk to use your data for Training and Fine-Tuning helps us make improvements to
        <br />
        the AI Models we use (including LLMs) in our current and future products and features.
    </>,
];

const USER_MANAGEMENT_TITLE = 'User management';
const SSO_DEFAULT_ROLE_LABEL = 'Set a default role for SSO login';
const SSO_DEFAULT_ROLE_INFORMATION =
    'Apply the following role to a new user logging in through an SSO service.';

const SPLUNK_RBAC_SOURCE = 'SPLUNK_EC';
const CRBAC_FALLBACK_LABEL = ['Allow alternative SSO login for Splunk Centralized RBAC users'];
const CRBAC_FALLBACK_INFORMATION = [
    <>
        If Splunk Cloud or Splunk Cloud Services are unavailable, your users can still log in using
        the alternative SSO login. Users actively using the Splunk Observability Cloud organization
        have the roles and capabilities they last logged in with. Users who have not logged in using
        Unified Identity within the past week use the default SSO role. To set up the alternative
        SSO login method, see{' '}
        <a
            href="https://help.splunk.com/en/splunk-observability-cloud/administer/splunk-platform-users/centralized-user-and-role-management"
            target="_blank"
            rel="noopener noreferrer"
        >
            Centralized user and role management
        </a>
        .
    </>,
];

const className = new BEMHelper('organization-preferences');

const msInADay = 24 * 60 * 60 * 1000; // 1 day in milliseconds

function convertMsToDays(milliseconds) {
    return milliseconds / msInADay;
}

function OrgPrefSection({ title, content }) {
    return (
        <div {...className('section-container')}>
            <Heading level={2} {...className('section-header')}>
                {title}
            </Heading>
            {content}
        </div>
    );
}

function OrganizationPreferences({ featureEnabled, editable }) {
    const [restrictTeamManagement, setRestrictTeamManagement] = useState(null);
    const [alertAutoClearDuration, setAlertAutoClearDuration] = useState(null);
    const [defaultTokenExpiration, setDefaultTokenExpiration] = useState('30');
    const [relatedContentLogsDisabled, setRelatedContentLogsDisabled] = useState(null);
    const [logsRCMappingsConsumptionConfiguration, setLogsRCMappingsConsumptionConfiguration] =
        useState(null);
    const [isValid, setIsValid] = useState(true);
    const [isValidDefaultTokenExpiration, setValidDefaultTokenExpiration] = useState(true);
    const [isCancelled, cancel] = useReducer((c) => c + 1, 0);
    const [isLoading, setIsLoading] = useState(true);
    const [initialTeamManagementSetting, setInitialTeamManagementSetting] = useState(null);
    const [initialAlertAutoClearDuration, setInitialAlertAutoClearDuration] = useState(null);
    const [initialDefaultTokenExpiration, setInitialDefaultTokenExpiration] = useState('30');
    const [initialRelatedContentLogsDisabled, setInitialRelatedContentLogsDisabled] =
        useState(null);

    const [
        initialLogsRCMappingsConsumptionConfiguration,
        setInitialLogsRCMappingsConsumptionConfiguration,
    ] = useState(null);

    const [initialAiAssistant, setInitialAiAssistant] = useState(null);
    const [initialAiAssistantShareData, setInitialAiAssistantShareData] = useState(null);
    const [orgPreferenceId, setOrgPreferenceId] = useState(null);
    const [syncState, setSyncState] = useState(null);
    const [aiAssistant, setAiAssistant] = useState(null);
    const [aiAssistantShareData, setAiAssistantShareData] = useState(null);

    // initial values set to empty strings for case-insensitive comparison
    const [initialDefaultSsoRole, setInitialDefaultSsoRole] = useState('');
    const [defaultSsoRole, setDefaultSsoRole] = useState('');

    const [isSplunkRbacOrg, setIsSplunkRbacOrg] = useState(false);
    const [initialSplunkIdpFallbackEnabled, setInitialSplunkIdpFallbackEnabled] = useState(null);
    const [splunkIdpFallbackEnabled, setSplunkIdpFallbackEnabled] = useState(null);

    const orgPreferenceService = OrganizationPreferenceService.useInstance();
    const userAnalyticsService = UserAnalyticsService.useInstance();

    const isUpdated = () => {
        return (
            restrictTeamManagement !== initialTeamManagementSetting ||
            alertAutoClearDuration !== initialAlertAutoClearDuration ||
            defaultTokenExpiration !== initialDefaultTokenExpiration ||
            relatedContentLogsDisabled !== initialRelatedContentLogsDisabled ||
            aiAssistant !== initialAiAssistant ||
            aiAssistantShareData !== initialAiAssistantShareData ||
            logsRCMappingsConsumptionConfiguration !==
                initialLogsRCMappingsConsumptionConfiguration ||
            // if the selected role vs. default role from API is just a case difference, do not show ChangeButtons
            defaultSsoRole.toLowerCase() !== initialDefaultSsoRole.toLowerCase() ||
            splunkIdpFallbackEnabled !== initialSplunkIdpFallbackEnabled
        );
    };

    useEffect(() => {
        if (!editable) {
            ngRoute.history?.replace('/NotFound');
        }
    }, [editable]);

    useEffect(() => {
        const fetchOrgData = async () => {
            const [organizationSettings, org] = await Promise.all([
                orgPreferenceService.get(),
                featureEnabled('splunkRbacLoginFallback')
                    ? orgPreferenceService.getOrg().catch(() => null)
                    : Promise.resolve(null),
            ]);
            setOrgPreferenceId(organizationSettings.sf_id);
            setInitialTeamManagementSetting(!!organizationSettings.sf_restrictTeamManagement);
            setRestrictTeamManagement(!!organizationSettings.sf_restrictTeamManagement);
            if (featureEnabled('logsRCIsEnabledOrDisabledByAdmin')) {
                setInitialRelatedContentLogsDisabled(
                    !!organizationSettings.sf_relatedContentLogsDisabled
                );
                setRelatedContentLogsDisabled(!!organizationSettings.sf_relatedContentLogsDisabled);
            }

            if (organizationSettings.sf_alertAutoClearDuration) {
                setInitialAlertAutoClearDuration(organizationSettings.sf_alertAutoClearDuration);
                setAlertAutoClearDuration(organizationSettings.sf_alertAutoClearDuration);
            }

            if (featureEnabled('tokenExpirationEnhancements')) {
                const defaultTime =
                    Math.floor(convertMsToDays(organizationSettings.sf_defaultTokenLifespanMs)) ||
                    30;
                setInitialDefaultTokenExpiration(defaultTime.toString());
                setDefaultTokenExpiration(defaultTime.toString());
            }

            if (featureEnabled('aiAssistant')) {
                setAiAssistant(!!organizationSettings.sf_ai_assistant_enabled);
                setAiAssistantShareData(
                    !!organizationSettings.sf_ai_assistant_customer_data_usage_enabled
                );
                setInitialAiAssistant(!!organizationSettings.sf_ai_assistant_enabled);
                setInitialAiAssistantShareData(
                    !!organizationSettings.sf_ai_assistant_customer_data_usage_enabled
                );
            }

            if (organizationSettings.sf_orgDefaultRole) {
                setInitialDefaultSsoRole(organizationSettings.sf_orgDefaultRole);
                setDefaultSsoRole(organizationSettings.sf_orgDefaultRole);
            }

            if (featureEnabled('splunkRbacLoginFallback')) {
                setIsSplunkRbacOrg(org?.rbacSource === SPLUNK_RBAC_SOURCE);
                setInitialSplunkIdpFallbackEnabled(
                    !!organizationSettings.sf_splunkIdpFallbackEnabled
                );
                setSplunkIdpFallbackEnabled(!!organizationSettings.sf_splunkIdpFallbackEnabled);
            }

            if (featureEnabled('entityToIndexMapping')) {
                const logsRCMappingsValue =
                    organizationSettings.sf_logsRCMappingsConsumptionConfiguration !== undefined
                        ? !!organizationSettings.sf_logsRCMappingsConsumptionConfiguration
                        : true;
                setInitialLogsRCMappingsConsumptionConfiguration(logsRCMappingsValue);
                setLogsRCMappingsConsumptionConfiguration(logsRCMappingsValue);
            }
            setIsLoading(false);
        };

        fetchOrgData();
    }, [featureEnabled, orgPreferenceService]);

    function handleSubmit() {
        setSyncState('APPLYING');

        const patch = {};
        if (restrictTeamManagement !== initialTeamManagementSetting) {
            patch.restrictTeamManagement = restrictTeamManagement;
        }
        if (alertAutoClearDuration !== initialAlertAutoClearDuration) {
            patch.alertAutoClearDuration = alertAutoClearDuration;
        }

        if (defaultTokenExpiration !== initialDefaultTokenExpiration) {
            const timeInMs = parseInt(defaultTokenExpiration) * msInADay;
            patch.defaultTokenExpiration = timeInMs;
        }

        if (relatedContentLogsDisabled !== initialRelatedContentLogsDisabled) {
            patch.relatedContentLogsDisabled = relatedContentLogsDisabled;
        }

        if (aiAssistant !== initialAiAssistant) {
            patch.aiAssistant = aiAssistant;
        }

        if (aiAssistantShareData !== initialAiAssistantShareData) {
            patch.aiAssistantShareData = aiAssistantShareData;
        }

        if (defaultSsoRole !== initialDefaultSsoRole) {
            patch.defaultSsoRole = defaultSsoRole;
        }

        if (splunkIdpFallbackEnabled !== initialSplunkIdpFallbackEnabled) {
            patch.splunkIdpFallbackEnabled = splunkIdpFallbackEnabled;
        }

        if (
            logsRCMappingsConsumptionConfiguration !== initialLogsRCMappingsConsumptionConfiguration
        ) {
            patch.logsRCMappingsConsumptionConfiguration = logsRCMappingsConsumptionConfiguration;
        }

        return orgPreferenceService
            .update(orgPreferenceId, patch)
            .then(() => {
                setSyncState('COMPLETED');
                const eventAction = restrictTeamManagement
                    ? 'team-controls-toggle-on'
                    : 'team-controls-toggle-off';
                userAnalyticsService.event('general organization settings', eventAction);
                setInitialTeamManagementSetting(restrictTeamManagement);
                setInitialAlertAutoClearDuration(alertAutoClearDuration);

                if (featureEnabled('logsRCIsEnabledOrDisabledByAdmin')) {
                    setInitialRelatedContentLogsDisabled(relatedContentLogsDisabled);
                }
                if (featureEnabled('tokenExpirationEnhancements')) {
                    setInitialDefaultTokenExpiration(defaultTokenExpiration);
                }
                if (featureEnabled('aiAssistant')) {
                    setInitialAiAssistant(aiAssistant);
                    setInitialAiAssistantShareData(aiAssistantShareData);
                }
                setInitialDefaultSsoRole(defaultSsoRole);
                if (featureEnabled('splunkRbacLoginFallback')) {
                    setInitialSplunkIdpFallbackEnabled(splunkIdpFallbackEnabled);
                }

                if (featureEnabled('entityToIndexMapping')) {
                    setInitialLogsRCMappingsConsumptionConfiguration(
                        logsRCMappingsConsumptionConfiguration
                    );
                }
            })
            .catch(() => {
                setSyncState('FAILED');
            })
            .finally(() => {
                setTimeout(function () {
                    setSyncState(null);
                }, 1000);
            });
    }

    function handleCancelChanges() {
        setRestrictTeamManagement(initialTeamManagementSetting);
        setAlertAutoClearDuration(initialAlertAutoClearDuration);
        setDefaultTokenExpiration(initialDefaultTokenExpiration);
        setRelatedContentLogsDisabled(initialRelatedContentLogsDisabled);
        setAiAssistant(initialAiAssistant);
        setAiAssistantShareData(initialAiAssistantShareData);
        setDefaultSsoRole(initialDefaultSsoRole);
        setSplunkIdpFallbackEnabled(initialSplunkIdpFallbackEnabled);
        setLogsRCMappingsConsumptionConfiguration(initialLogsRCMappingsConsumptionConfiguration);
        cancel();
    }

    function handleRestrictTeamManagementChanges() {
        setRestrictTeamManagement(!restrictTeamManagement);
    }

    function handleRelatedContentLogsDisabledChanges() {
        const newRelatedContentLogsDisabled = !relatedContentLogsDisabled;
        setRelatedContentLogsDisabled(newRelatedContentLogsDisabled);
    }

    function handleLogsRCMappingsConsumptionConfigurationChanges() {
        setLogsRCMappingsConsumptionConfiguration(!logsRCMappingsConsumptionConfiguration);
    }

    function handleAIAssistantChange() {
        const newAiAssistantVal = !aiAssistant;
        setAiAssistant(newAiAssistantVal);

        // turn off data sharing automatically if assistant is turned off.
        if (!newAiAssistantVal) {
            setAiAssistantShareData(false);
        }
    }

    function handleAIAssistantDataSharingChange() {
        setAiAssistantShareData(!aiAssistantShareData);
    }

    function handleSplunkIdpFallbackEnabledChanges() {
        setSplunkIdpFallbackEnabled(!splunkIdpFallbackEnabled);
    }

    const shouldEnableActionButtons = isUpdated();
    const themeKey = useLegacyThemeServiceKey();

    return (
        <ThemeProvider colorScheme={themeKey}>
            <StepLayout
                nextButtonLabel={APPLY_CHANGES}
                backButtonLabel={CANCEL}
                isNextEnabled={
                    shouldEnableActionButtons && isValid && isValidDefaultTokenExpiration
                }
                isBackEnabled={shouldEnableActionButtons}
                onNextClick={handleSubmit}
                onBackClick={handleCancelChanges}
                isLoading={isLoading}
            >
                <section
                    {...className()}
                    {...(featureEnabled('magneticPageHeader') && className('compact'))}
                >
                    <div {...className('content-container')}>
                        {featureEnabled('magneticPageHeader') ? (
                            <PageHeaderWrapper
                                navigation={[
                                    {
                                        id: featureEnabled('o11yNavDrawer')
                                            ? Sitemaps.OrganizationPreferences.map.id
                                            : 'General Settings',
                                        label: Sitemaps.OrganizationPreferences.map.label,
                                    },
                                ]}
                            />
                        ) : (
                            <h4 {...className('page-title')}>
                                {featureEnabled('o11yNavDrawer')
                                    ? Sitemaps.OrganizationPreferences.map.id
                                    : ORGANIZATION_SETTINGS_TITLE}
                            </h4>
                        )}
                        <div {...className('content')}>
                            <SavingStatus status={syncState} />
                            <OrgPrefSection
                                title={ALERT_MANAGEMENT_TITLE}
                                content={
                                    <InformationDurationInput
                                        key={isCancelled}
                                        label={ALERT_AUTO_CLEAR_LABEL}
                                        value={alertAutoClearDuration}
                                        onChange={(duration, isValidDuration) => {
                                            setIsValid(isValidDuration);
                                            setAlertAutoClearDuration(duration);
                                        }}
                                        additionalInfo={ALERT_AUTO_CLEAR_INFORMATION}
                                    />
                                }
                            />
                            {featureEnabled('tokenExpirationEnhancements') && (
                                <OrgPrefSection
                                    title={TOKEN_MANAGEMENT_TITLE}
                                    content={
                                        <DefaultTokenExpirationConfiguration
                                            label={ACCESS_TOKENS_LABEL}
                                            value={defaultTokenExpiration}
                                            onChange={(
                                                defaultExpirationTokenValue,
                                                isValidExpiryDate
                                            ) => {
                                                setValidDefaultTokenExpiration(isValidExpiryDate);
                                                setDefaultTokenExpiration(
                                                    defaultExpirationTokenValue
                                                );
                                            }}
                                            additionalInfo={ACCESS_TOKEN_INFORMATION}
                                        />
                                    }
                                />
                            )}
                            <OrgPrefSection
                                title={USER_MANAGEMENT_TITLE}
                                content={
                                    <div {...className('user-management')}>
                                        <DefaultSsoRoleConfiguration
                                            label={SSO_DEFAULT_ROLE_LABEL}
                                            value={defaultSsoRole}
                                            onChange={(defaultSsoRole) => {
                                                setDefaultSsoRole(defaultSsoRole);
                                            }}
                                            additionalInfo={SSO_DEFAULT_ROLE_INFORMATION}
                                            newRbacCustomRoleEnabled={featureEnabled(
                                                'newRbacCustomRole'
                                            )}
                                        />
                                        {featureEnabled('splunkRbacLoginFallback') &&
                                            isSplunkRbacOrg && (
                                                <InformationCheckbox
                                                    name="allow-crbac-fallback-login"
                                                    label={CRBAC_FALLBACK_LABEL}
                                                    isSelected={splunkIdpFallbackEnabled}
                                                    onCheckboxClick={
                                                        handleSplunkIdpFallbackEnabledChanges
                                                    }
                                                    additionalInfo={CRBAC_FALLBACK_INFORMATION}
                                                />
                                            )}
                                    </div>
                                }
                            />
                            {featureEnabled('writepermissions') && (
                                <OrgPrefSection
                                    title={TEAM_MANAGEMENT_TITLE}
                                    content={
                                        <InformationCheckbox
                                            name="restrict-team-access"
                                            label={TEAM_CHECKBOX_LABEL}
                                            isSelected={restrictTeamManagement}
                                            onCheckboxClick={handleRestrictTeamManagementChanges}
                                            additionalInfo={TEAM_MANAGEMENT_INFORMATION}
                                        />
                                    }
                                />
                            )}
                            {!featureEnabled('logsConfiguration') &&
                                (featureEnabled('logsRCIsEnabledOrDisabledByAdmin') ||
                                    featureEnabled('entityToIndexMapping')) && (
                                    <OrgPrefSection
                                        title={RELATED_CONTENT_MANAGEMENT_TITLE}
                                        content={
                                            <>
                                                {featureEnabled(
                                                    'logsRCIsEnabledOrDisabledByAdmin'
                                                ) && (
                                                    <InformationCheckbox
                                                        name="activate-related-content-logs"
                                                        label={RC_CHECKBOX_LABEL}
                                                        isSelected={!relatedContentLogsDisabled}
                                                        onCheckboxClick={
                                                            handleRelatedContentLogsDisabledChanges
                                                        }
                                                        additionalInfo={RC_MANAGEMENT_INFORMATION}
                                                    />
                                                )}
                                                {featureEnabled('entityToIndexMapping') && (
                                                    <InformationCheckbox
                                                        name="activate-rc-mappings-consumption"
                                                        label={LOGS_RCMAPPINGS_CONSUMPTION_LABEL}
                                                        isSelected={
                                                            logsRCMappingsConsumptionConfiguration
                                                        }
                                                        disabled={
                                                            featureEnabled(
                                                                'logsRCIsEnabledOrDisabledByAdmin'
                                                            ) && relatedContentLogsDisabled
                                                        }
                                                        onCheckboxClick={
                                                            handleLogsRCMappingsConsumptionConfigurationChanges
                                                        }
                                                        additionalInfo={
                                                            LOGS_RCMAPPINGS_CONSUMPTION_INFORMATION
                                                        }
                                                    />
                                                )}
                                            </>
                                        }
                                    />
                                )}
                            {featureEnabled('aiAssistant') && (
                                <OrgPrefSection
                                    title={AI_ASSISTANT_MANAGEMENT_TITLE}
                                    content={
                                        <>
                                            {aiAssistant ? (
                                                <div>
                                                    AI Assistant and additional AI features are
                                                    active
                                                    <div {...className('ai-assistant-info')}>
                                                        The AI Assistant and additional AI features
                                                        are active for your organization, delivering
                                                        insights into
                                                        <br />
                                                        your Observability data. To deactivate them,
                                                        contact Splunk Support.{' '}
                                                        <a
                                                            href="https://help.splunk.com/en/splunk-observability-cloud/splunk-ai-assistant/ai-assistant-in-observability-cloud#use-cases-0"
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                        >
                                                            AI Assistant documentation
                                                        </a>
                                                    </div>
                                                    <br />
                                                </div>
                                            ) : (
                                                <InformationCheckbox
                                                    name="enable-ai-assistant"
                                                    label={ENABLE_AI_ASSISTANT_LABEL}
                                                    isSelected={aiAssistant}
                                                    onCheckboxClick={handleAIAssistantChange}
                                                    additionalInfo={ENABLE_AI_ASSISTANT_INFORMATION}
                                                />
                                            )}
                                            <div>{aiAssistant}</div>
                                            <div {...className('ai-assistant-management')}>
                                                <InformationCheckbox
                                                    name="ai-assistant-data-sharing" //
                                                    disabled={!aiAssistant}
                                                    label={ENABLE_AI_ASSISTANT_DATA_SHARING}
                                                    isSelected={aiAssistantShareData}
                                                    onCheckboxClick={
                                                        handleAIAssistantDataSharingChange
                                                    }
                                                    additionalInfo={ENABLE_DATA_SHARING_INFORMATION}
                                                />
                                            </div>
                                            <div>{aiAssistantShareData}</div>
                                        </>
                                    }
                                />
                            )}
                        </div>
                    </div>
                </section>
            </StepLayout>
        </ThemeProvider>
    );
}

/**
 * A wrapper component for organization preferences that provides user context and
 * ensures access protection based on specified endpoints.
 *
 * @param {Object} props - The properties passed to the OrganizationPreferences component.
 * @returns {JSX.Element} The wrapped OrganizationPreferences component.
 *
 * @component
 * @example
 * return (
 *   <OrganizationPreferencesWrapper {...props} />
 * );
 *
 */
const OrganizationPreferencesWrapper = (props) => {
    const currentUser = AngularInjector.useInjectedClass('currentUser');
    return (
        <CurrentUserStoreProvider serviceStoreInstance={currentUser}>
            <ProtectedComponentWrapper
                endpoints={[
                    {
                        path: '/v2/organization/settings',
                        httpMethod: HTTPMethod.GET,
                    },
                ]}
                fallbackUi={<ForbiddenComponent />}
            >
                <OrganizationPreferences {...props} />
            </ProtectedComponentWrapper>
        </CurrentUserStoreProvider>
    );
};

OrganizationPreferences.propTypes = {
    featureEnabled: PropTypes.func,
    currentUser: PropTypes.func,
    editable: PropTypes.bool,
};

export default OrganizationPreferencesWrapper;
